import { memo, useMemo, useState, type UIEventHandler } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import breakpoint from '~/utils/style-utils';
import { LabelBold } from '@comeon/ui';
import {
	selectCasinoSubMenuItems,
	selectEnableLiveCasino3,
	selectIsDesktop,
	type SubmenuItem
} from '~/components/brand/ducks/selectors';
import trackTournamentEvent from '~/components/casino-shared/tournaments/utils/track-tournament-event';
import { PillStyles, PillsContainerStyles } from '~/components/filtering-pills';
import Scroller from '~/components/casino-shared/scroller';
import useCasinoRoutes from '~/components/router/use-casino-routes';
import { ROUTE_TOURNAMENT_LOBBY } from '~/components/casino-shared/tournaments/routes';
import { FiltersInner } from '../navbar/navbar.styles';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import { debounce } from '@comeon/utility';
import { useCasinoSharedSelector } from '../ducks/types';
import useDisplayGlobalNavigation from '~/components/global-navigation/use-display-global-navigation';

// override css variables for new submenu design (colors are not same with filter pills)
const Container = styled.div`
	--filter-pills-text-color: var(--submenu-item-text-color);
	--filter-pills-text-color-active: var(--submenu-item-text-color-active);
	--filter-pills-bg: var(--submenu-item-bg);
	--filter-pills-bg-active: var(--submenu-item-bg-active);
	${breakpoint.lg`
		--filter-pills-text-color: var(--submenu-item-text-color--desktop);
		--filter-pills-text-color-active: var(--submenu-item-text-color-active--desktop);
		--filter-pills-bg: var(--submenu-item-bg--desktop);
		--filter-pills-bg-active: var(--submenu-item-bg-active--desktop);
	`};
`;

const PillsContainer = styled(PillsContainerStyles)`
	background-color: transparent;
`;

type GetSubNavPillStylesProps = {
	$active?: boolean;
	$displayGlobalNavigation?: boolean;
};

const getSubNavPillBackgroundMobile = ({ $active, $displayGlobalNavigation }: GetSubNavPillStylesProps) => {
	const backgroundColor = $displayGlobalNavigation ? 'var(--subnavigation-item-button-bg)' : 'var(--filter-pills-bg)';

	return $active ? 'var(--filter-pills-bg-active)' : backgroundColor;
};

const getSubNavPillBackgroundDesktop = ({ $active }: GetSubNavPillStylesProps) => {
	return $active ? 'var(--filter-pills-bg-active)' : 'var(--filter-pills-bg)';
};

const getSubNavPillColorMobile = ({ $active, $displayGlobalNavigation }: GetSubNavPillStylesProps) => {
	const color = $displayGlobalNavigation ? 'var(--subnavigation-item-text-color)' : 'var(--filter-pills-text-color)';

	return $active ? 'var(--filter-pills-text-color-active)' : color;
};
const getSubNavPillColorDesktop = ({ $active }: GetSubNavPillStylesProps) => {
	return $active ? 'var(--filter-pills-text-color-active)' : 'var(--filter-pills-text-color)';
};

const SubNavPill = styled(PillStyles)<GetSubNavPillStylesProps>`
	height: 36px;
	margin-right: 12px;
	border-radius: 12px;
	border: 1px solid
		${({ $active }) => ($active ? `var(--submenu-item-border-color-active)` : `var(--submenu-item-border-color)`)};
	background-color: ${props => getSubNavPillBackgroundMobile(props)};
	color: ${props => getSubNavPillColorMobile(props)};

	.pill-icon {
		background-color: ${props => getSubNavPillColorMobile(props)};
	}

	h5 {
		color: ${props => getSubNavPillColorMobile(props)};
		font: var(--label-bold);
	}

	${({ $active }) => breakpoint.lg`
		border-radius: var(--brand-filter-pills-border-radius, 25px);
		margin-right: 8px;
		border: 1px solid ${
			$active ? `var(--submenu-item-border-color-active--desktop)` : `var(--submenu-item-border-color--desktop)`
		};
	`}

	${props => breakpoint.lg`	
		background-color: ${getSubNavPillBackgroundDesktop(props)};
		color: ${getSubNavPillColorDesktop(props)};

		.pill-icon {
			background-color: ${getSubNavPillColorDesktop(props)};
		}

		h5 {
			color: ${getSubNavPillColorDesktop(props)};
		}
	`}
`;

const TournamentPill = styled(SubNavPill)`
	cursor: pointer;
`;

const PillContent = styled.div`
	display: flex;
	align-items: center;
	color: inherit;
`;

const PillName = styled(LabelBold)`
	color: inherit;
`;

const PillIcon = styled.div<{ imageSrc: string }>`
	width: 20px;
	height: 36px;
	margin-right: 5px;
	z-index: 0;
	background-size: cover;
	background-color: var(--filter-pills-text-color);
	mask: url(${props => props.imageSrc}) no-repeat 50% 50%;
	&:active {
		background-color: var(--filter-pills-text-color-active);
	}
`;

type Props = {
	isLive: boolean;
	filtersDataAt: string;
};

const Subnavigation = memo(({ isLive, filtersDataAt }: Props) => {
	const { [isLive ? 'live_casino' : 'casino']: subMenuItems } =
		useCasinoSharedSelector(selectCasinoSubMenuItems) || {};
	const transformPath = useContextualPathTransformer();
	const [hideRightShadow, setHideRightShadow] = useState(false);
	const [hideLeftShadow, setHideLeftShadow] = useState(true);
	const location = useLocation();
	const enableLiveCasino3 = useCasinoSharedSelector(selectEnableLiveCasino3);
	const { displayGlobalNavigation } = useDisplayGlobalNavigation();
	const isDesktop = useCasinoSharedSelector(selectIsDesktop);
	const {
		ROUTE_CASINO,
		ROUTE_CASINO_EXPLORE,
		ROUTE_CASINO_ALL_GAMES,
		ROUTE_CASINO_MY_GAMES_LOVED,
		ROUTE_LIVE_CASINO,
		ROUTE_LIVE_CASINO_ALL_GAMES,
		ROUTE_LIVE_CASINO_EXPLORE,
		ROUTE_LIVE_CASINO_MY_GAMES_LOVED
	} = useCasinoRoutes();

	const pathMap = useMemo(() => {
		const liveCasinoPathMapping = new Map([
			['EXPLORE', ROUTE_LIVE_CASINO_EXPLORE],
			['ALL_GAMES', ROUTE_LIVE_CASINO_ALL_GAMES],
			['MY_GAMES', ROUTE_LIVE_CASINO_MY_GAMES_LOVED]
		]);

		const casinoPathMapping = new Map([
			['EXPLORE', ROUTE_CASINO_EXPLORE],
			['ALL_GAMES', ROUTE_CASINO_ALL_GAMES],
			['MY_GAMES', ROUTE_CASINO_MY_GAMES_LOVED]
		]);

		return isLive ? liveCasinoPathMapping : casinoPathMapping;
	}, [
		ROUTE_CASINO_ALL_GAMES,
		ROUTE_CASINO_EXPLORE,
		ROUTE_CASINO_MY_GAMES_LOVED,
		ROUTE_LIVE_CASINO_ALL_GAMES,
		ROUTE_LIVE_CASINO_EXPLORE,
		ROUTE_LIVE_CASINO_MY_GAMES_LOVED,
		isLive
	]);

	const renderItem = (item: SubmenuItem, index: number) => {
		if (item.type === 'TOURNAMENT') {
			return (
				<TournamentPill
					onClick={() => {
						trackTournamentEvent('explore-page', 'click-in-tournament-submenu');
					}}
					data-at={`submenu-item-${index}`}
					key={item.urlPath}
					href={transformPath(ROUTE_TOURNAMENT_LOBBY)}
				>
					<LabelBold data-at="tournament-label">{item.name}</LabelBold>
				</TournamentPill>
			);
		}

		let path = '';

		const casinoType = isLive ? ROUTE_LIVE_CASINO : ROUTE_CASINO;
		path = pathMap.get(item.type) ?? `${casinoType}/submenu/${item.urlPath}`;

		return (
			<SubNavPill
				key={path}
				href={path}
				data-at={`submenu-item-${index}`}
				$displayGlobalNavigation={displayGlobalNavigation}
				$active={location.pathname === path}
			>
				<PillContent>
					{item.image && <PillIcon className="pill-icon" imageSrc={item.image} />}
					<PillName>{item.name}</PillName>
				</PillContent>
			</SubNavPill>
		);
	};

	const handleScroll: UIEventHandler<HTMLDivElement> = e => {
		if (!(e.target instanceof HTMLElement)) return;
		// Remove shadow when scrolled to end
		setHideRightShadow(Math.abs(e.target.scrollWidth - e.target.scrollLeft - e.target.clientWidth) < 2);
		setHideLeftShadow(Math.abs(e.target.scrollLeft) < 2);
	};

	const debouncedScrollHandler = debounce(handleScroll, 5);

	// if LC3 features are enabled and device is mobile then we are filtering out my games and all games submenu items
	// TODO: filtering my games should be on backend
	const filterExploreSubmenu = (item: SubmenuItem) =>
		displayGlobalNavigation && !isDesktop ? item.type !== 'EXPLORE' : true;
	const filterAllGamesSubmenu = (item: SubmenuItem) =>
		enableLiveCasino3 && isLive ? item.type !== 'ALL_GAMES' : true;
	const filterMyGamesSubmenu = (item: SubmenuItem) =>
		enableLiveCasino3 && !isDesktop ? item.type !== 'MY_GAMES' : true;
	const filteredItems = subMenuItems
		?.filter(filterAllGamesSubmenu)
		?.filter(filterMyGamesSubmenu)
		.filter(filterExploreSubmenu);

	return (
		<FiltersInner dataAt={filtersDataAt} hideRightShadow={hideRightShadow} hideLeftShadow={hideLeftShadow}>
			<Scroller onScroll={debouncedScrollHandler}>
				<Container>
					<PillsContainer disablePadding overflowVisible>
						{filteredItems?.map(renderItem)}
					</PillsContainer>
				</Container>
			</Scroller>
		</FiltersInner>
	);
});

Subnavigation.displayName = 'Subnavigation';

export default Subnavigation;
